import {
  PAYEE_SLOT_AFTER,
  PAYEE_SLOT_BEFORE,
} from '../../../W9/shared/w9Template';
import { newFieldId } from '../../shared/fieldIds';
import { makePaletteField } from '../../shared/fieldTypes';
import {
  accountInfoIds,
  addAccountInfo,
  removeAccountInfo,
} from './accountInfo';
import { fieldFromSaved } from './savedFields';
import { snapshotOf } from './snapshot';
import { applySortable } from './sortableBridge';

// The builder's state (build plan §5.5). Pure: ids are minted by the
// action creators below (the reducer only falls back to minting one when
// an action arrives without it).
//
// state = { formId, revision, status, archived, kind, lockedFieldIds,
//           saved, working, selectedId, insertTarget, pane, errors,
//           localAssetUrls }
// insertTarget: { mode: 'before' | 'after', fieldId } | { mode: 'end' } | null
//   (null → after the selected field, else at the end)
// pane (phones): 'canvas' | 'edit' | 'settings'
// kind: 'custom' | 'w9' (the form's kind, from the server)
// lockedFieldIds (w9-plan §6.5): fields the admin can't add to, move,
//   change, duplicate or remove. Default [] (every Forms behaviour as
//   before). With any locked id, a new field can only land in the payee
//   slot (after PAYEE_SLOT_AFTER, before PAYEE_SLOT_BEFORE), and a sort
//   that would move a locked field or push a field out of the slot is
//   refused.

const lockedListOf = (list) =>
  Array.isArray(list) ? list.filter((id) => typeof id === 'string') : [];

export const initState = (form, { lockedFieldIds } = {}) => {
  const snap = snapshotOf(form);
  return {
    formId: form.id,
    revision: form.revision,
    status: form.status,
    archived: !!form.archived_at,
    kind: form.kind === 'w9' ? 'w9' : 'custom',
    lockedFieldIds: lockedListOf(lockedFieldIds),
    saved: snap,
    working: snap,
    selectedId: null,
    insertTarget: null,
    pane: 'canvas',
    errors: {},
    localAssetUrls: {},
  };
};

const lockedOf = (state) => (state && state.lockedFieldIds) || [];

export const isLockedId = (state, id) => !!id && lockedOf(state).includes(id);

const withoutKey = (object, key) => {
  if (!(key in object)) return object;
  const next = { ...object };
  delete next[key];
  return next;
};

const setFields = (state, fields, extra) => ({
  ...state,
  working: { ...state.working, fields },
  ...extra,
});

const indexOf = (fields, id) =>
  id ? fields.findIndex((f) => f.id === id) : -1;

// The payee slot's insert positions, [start, end], when the form has
// locked fields (w9-plan §6.5): after PAYEE_SLOT_AFTER and before
// PAYEE_SLOT_BEFORE. Without locked fields the whole list is open.
export const slotBounds = (fields, lockedFieldIds) => {
  if (!lockedListOf(lockedFieldIds).length)
    return { start: 0, end: fields.length };
  const after = indexOf(fields, PAYEE_SLOT_AFTER);
  const before = indexOf(fields, PAYEE_SLOT_BEFORE);
  const start = after >= 0 ? after + 1 : 0;
  const end = before >= 0 ? Math.max(before, start) : fields.length;
  return { start, end };
};

const clampToSlot = (fields, at, lockedFieldIds) => {
  const { start, end } = slotBounds(fields, lockedFieldIds);
  return Math.min(Math.max(at, start), end);
};

// Where a new field goes: the explicit target, else after the selected
// field, else at the end — kept inside the payee slot when fields are
// locked (so "at the end" means the slot's end).
export const insertionIndex = (fields, target, selectedId, lockedFieldIds) => {
  let at;
  const explicit = target ? indexOf(fields, target.fieldId) : -1;
  if (target && target.mode === 'end') at = fields.length;
  else if (explicit >= 0)
    at = target.mode === 'before' ? explicit : explicit + 1;
  else {
    const selected = indexOf(fields, selectedId);
    at = selected >= 0 ? selected + 1 : fields.length;
  }
  return clampToSlot(fields, at, lockedFieldIds);
};

const insertField = (state, field, action) => {
  if (!field) return state;
  const { fields } = state.working;
  const target =
    action.target !== undefined ? action.target : state.insertTarget;
  const at = insertionIndex(fields, target, state.selectedId, lockedOf(state));
  const next = [...fields.slice(0, at), field, ...fields.slice(at)];
  return setFields(state, next, {
    selectedId: field.id,
    insertTarget: null,
    pane: action.mobile ? 'edit' : state.pane,
  });
};

const patchField = (field, patch) => {
  const next = { ...field, ...patch, id: field.id, type: field.type };
  if (patch.config) next.config = { ...field.config, ...patch.config };
  return next;
};

// Forget selection / target / pane that point at fields no longer there.
const dropStale = (state) => {
  const ids = state.working.fields.map((f) => f.id);
  const selectedId = ids.includes(state.selectedId) ? state.selectedId : null;
  const target = state.insertTarget;
  const insertTarget =
    target && target.fieldId && !ids.includes(target.fieldId) ? null : target;
  const pane = state.pane === 'edit' && !selectedId ? 'canvas' : state.pane;
  return { ...state, selectedId, insertTarget, pane };
};

const cloneConfig = (config) => {
  const out = {};
  Object.keys(config || {}).forEach((key) => {
    out[key] = Array.isArray(config[key]) ? config[key].slice() : config[key];
  });
  return out;
};

const moveField = (state, { id, direction }) => {
  const { fields } = state.working;
  const from = indexOf(fields, id);
  const to = from + (direction === 'up' ? -1 : 1);
  if (from < 0 || to < 0 || to >= fields.length) return state;
  // A locked field never moves, and nothing swaps past one.
  if (isLockedId(state, id) || isLockedId(state, fields[to].id)) return state;
  const next = fields.slice();
  [next[from], next[to]] = [next[to], next[from]];
  return setFields(state, next);
};

const duplicateField = (state, { id, newId }) => {
  const { fields } = state.working;
  const at = indexOf(fields, id);
  if (at < 0 || isLockedId(state, id)) return state;
  const source = fields[at];
  // A copy is the admin's own field, never part of the account-info set.
  const copy = {
    ...source,
    id: newId || newFieldId(),
    acct: false,
    config: cloneConfig(source.config),
  };
  const next = [...fields.slice(0, at + 1), copy, ...fields.slice(at + 1)];
  return setFields(state, next, { selectedId: copy.id });
};

// With locked fields, a sort result must keep every locked field where it
// was relative to the others and every other field inside the payee slot.
export const keepsLockedLayout = (before, after, lockedFieldIds) => {
  const locked = lockedListOf(lockedFieldIds);
  if (!locked.length) return true;
  const lockedIds = (list) =>
    list.filter((f) => locked.includes(f.id)).map((f) => f.id);
  if (lockedIds(before).join(' ') !== lockedIds(after).join(' ')) return false;
  const { start, end } = slotBounds(after, locked);
  return after.every(
    (f, i) => locked.includes(f.id) || (i >= start && i < end)
  );
};

const applySorted = (state, { items }) => {
  const result = applySortable(state.working.fields, items || []);
  if (!result) return state;
  if (!keepsLockedLayout(state.working.fields, result.fields, lockedOf(state)))
    return state;
  return setFields(state, result.fields, {
    selectedId: result.newId || state.selectedId,
  });
};

const toggleAccountInfo = (state, { on, ids }) => {
  // A W-9 can't pre-fill guests; the account-info set never joins it.
  if (lockedOf(state).length) return state;
  const { fields } = state.working;
  const next = on ? addAccountInfo(fields, ids) : removeAccountInfo(fields);
  if (next === fields) return state;
  return dropStale(setFields(state, next));
};

const saved = (state, { form, sent }) => {
  const snap = snapshotOf(form);
  // Edits made while the save was in flight stay unsaved.
  const keepWorking = sent && state.working !== sent;
  return dropStale({
    ...state,
    formId: form.id,
    revision: form.revision,
    status: form.status,
    archived: !!form.archived_at,
    kind: form.kind === 'w9' ? 'w9' : state.kind,
    saved: snap,
    working: keepWorking ? state.working : snap,
    errors: {},
  });
};

const setStatus = (state, { status, archived, revision }) => ({
  ...state,
  status: status || state.status,
  archived: archived === undefined ? state.archived : !!archived,
  revision: revision === undefined ? state.revision : revision,
});

const HANDLERS = {
  INIT: (_state, { form, lockedFieldIds }) =>
    initState(form, { lockedFieldIds }),
  SAVED: saved,
  SET_STATUS: setStatus,
  SET_SETTING: (state, { key, value }) => ({
    ...state,
    working: { ...state.working, [key]: value },
    errors: withoutKey(state.errors, key),
  }),
  ADD_FIELD: (state, action) =>
    insertField(
      state,
      makePaletteField(action.fieldType, action.id || newFieldId()),
      action
    ),
  ADD_SAVED_FIELD: (state, action) =>
    insertField(
      state,
      fieldFromSaved(action.savedField, action.id || newFieldId()),
      action
    ),
  UPDATE_FIELD: (state, { id, patch }) => {
    if (indexOf(state.working.fields, id) < 0 || isLockedId(state, id))
      return state;
    const fields = state.working.fields.map((f) =>
      f.id === id ? patchField(f, patch || {}) : f
    );
    return setFields(state, fields, {
      errors: withoutKey(state.errors, `definition.${id}`),
    });
  },
  REMOVE_FIELD: (state, { id }) => {
    const { fields } = state.working;
    if (indexOf(fields, id) < 0 || isLockedId(state, id)) return state;
    return dropStale(
      setFields(
        state,
        fields.filter((f) => f.id !== id),
        { errors: withoutKey(state.errors, `definition.${id}`) }
      )
    );
  },
  DUPLICATE_FIELD: duplicateField,
  MOVE_FIELD: moveField,
  APPLY_SORTABLE: applySorted,
  TOGGLE_ACCOUNT_INFO: toggleAccountInfo,
  SELECT: (state, { id, pane }) => ({
    ...state,
    selectedId: id || null,
    pane: pane || state.pane,
  }),
  SET_PANE: (state, { pane }) => ({
    ...state,
    pane,
    selectedId: pane === 'settings' ? null : state.selectedId,
  }),
  SET_INSERT_TARGET: (state, { target }) => ({
    ...state,
    insertTarget: target || null,
  }),
  SET_ERRORS: (state, { errors }) => ({ ...state, errors: errors || {} }),
  SET_LOCAL_ASSET: (state, { assetId, url }) => ({
    ...state,
    localAssetUrls: { ...state.localAssetUrls, [assetId]: url },
  }),
  DISCARD: (state) => ({
    ...state,
    working: state.saved,
    selectedId: null,
    insertTarget: null,
    pane: 'canvas',
    errors: {},
  }),
};

export const ACTIONS = Object.keys(HANDLERS);

const builderReducer = (state, action) => {
  const handler = action && HANDLERS[action.type];
  if (!handler) return state;
  if (!state && action.type !== 'INIT') return state;
  return handler(state, action);
};

export default builderReducer;

// ---- Action creators (the impure bits: new ids) --------------------------

export const addFieldAction = (fieldType, { target, mobile } = {}) => ({
  type: 'ADD_FIELD',
  fieldType,
  id: newFieldId(),
  target,
  mobile: !!mobile,
});

export const addSavedFieldAction = (savedField, { target, mobile } = {}) => ({
  type: 'ADD_SAVED_FIELD',
  savedField,
  id: newFieldId(),
  target,
  mobile: !!mobile,
});

export const duplicateFieldAction = (id) => ({
  type: 'DUPLICATE_FIELD',
  id,
  newId: newFieldId(),
});

export const toggleAccountInfoAction = (on) => ({
  type: 'TOGGLE_ACCOUNT_INFO',
  on: !!on,
  ids: on ? accountInfoIds() : undefined,
});
